import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../AdminLayout.tsx';
import { adminFetch } from '../../../utils/adminFetch.ts';

interface UserData {
  email: string;
  password: string;
  is_active: boolean;
  credits: number;
  name?: string;
  googleId?: string;
}

const UserCreate: React.FC = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState<UserData>({
    email: '',
    password: '',
    is_active: true,
    credits: 0,
    name: '',
    googleId: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    setUserData(prev => ({
      ...prev,
      [name]: type === 'checkbox' 
        ? (e.target as HTMLInputElement).checked 
        : type === 'number' 
          ? Number(value) 
          : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      await adminFetch('/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });
      
      navigate('/admin/users');
    } catch (err: any) {
      console.error('Error creating user:', err.message);
      setError(err.message || 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminLayout title="Create New User">
      <div className="max-w-2xl mx-auto bg-base rounded-lg shadow-md p-6">
        {error && (
          <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <p>{error}</p>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-dark mb-2">
              Email Address <span className="text-red-600">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={userData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-upload-button-pressed"
              placeholder="user@example.com"
            />
          </div>
          
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-dark mb-2">
              Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={userData.name}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-upload-button-pressed"
              placeholder="John Doe"
            />
          </div>
          
          <div>
            <label htmlFor="googleId" className="block text-sm font-medium text-gray-dark mb-2">
              Google ID
            </label>
            <input
              id="googleId"
              name="googleId"
              type="text"
              value={userData.googleId}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-upload-button-pressed"
              placeholder="Google ID (optional)"
            />
            <p className="text-xs text-gray-dark mt-1">Leave empty if not using Google authentication</p>
          </div>
          
          <div>
            <label htmlFor="credits" className="block text-sm font-medium text-gray-dark mb-2">
              Credits
            </label>
            <input
              id="credits"
              name="credits"
              type="number"
              min="0"
              value={userData.credits}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-upload-button-pressed"
            />
          </div>
          
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/admin/users')}
              className="px-4 py-2 border rounded-lg hover:bg-gray-light"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-upload-button-pressed text-white rounded-lg hover:bg-upload-button-hover hover:text-black disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default UserCreate;
